import { useState } from 'react';
import { getScopeRole } from '@/services/current-context';
import { useSearchParams } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { Modal } from '@/components/ui/modal';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from '@/components/ui/tooltip';
import { OrderDownloadAsset } from './OrderDownloadAsset';
import { buildZipNameBase } from '../../property-assets-management/download-assets/downloadNaming';
import { isDeliveredOrderStatus } from './model/orderCell';
import assetsSyncFailed from '../../../asset/images/assets-sync-failed.png';
import assetsSyncSuccess from '../../../asset/images/assets-sync-success.png';
import { Download, Eye, RefreshCw } from 'lucide-react';
import progressSpinner from '../../../asset/images/progress-spinner.svg';
import SyncAssets, {
  type SyncAssetsProps
} from '../../property-assets-management/syncAssets/SyncAssets';
import FailedAssetSync from '../../property-assets-management/failed-asset-sync/FailedAssetSync';
import type { SyncFailure } from '../../property-assets-management/failed-asset-sync/syncFailure';
import { IconComponent } from '../../../util/icons';
import { useIntegrationStatus } from '../../pms/hooks/useIntegrationStatus';
import { useSessionPmsAccount } from '../../pms/hooks/useSessionPmsAccount';
import { ACCOUNT_FAILED_SYNC_ACTIONS } from '../../property-assets-management/pmsNoticeActions';
import { useOrgWatermarkAvailable } from '@/hooks/useOrgWatermarkAvailable';

import dayjs from 'dayjs';

import localizedFormat from 'dayjs/plugin/localizedFormat';
import { useNavigationService } from '../../../services/navigate-service';
import { orderRowAssetsNav } from '../orderRowAssetsNav';

import { Typography } from '@/components/ui/typography';
import { canSync } from '@/util/roles';
import type { AssetSelection } from '../../../types/asset.types';
import type {
  OrderPropertyResponse,
  OrderService
} from '../../../types/property.types';

// The in-account orders table answers "is a watermark on file?" from the
// session's account, as SyncAssets did itself before DV-3345. A component, not
// a hook call at the top of OrderCellAssets, so the read still mounts with the
// modal rather than with every table row. (OrderDownloadAsset does the same.)
const SessionSyncAssets = (
  props: Omit<SyncAssetsProps, 'watermarkAvailable'>
) => {
  const watermarkAvailable = useOrgWatermarkAvailable();
  return <SyncAssets {...props} watermarkAvailable={watermarkAvailable} />;
};

// DV-2290: the one service whose delivered orders have no assets to manage.
const ASSET_TRANSFER_SLUG = 'asset_transfer';

/**
 * Data shape for OrderCellAssets.
 * Callers (e.g. propertyListTransform) pass generic Record<string, unknown>.
 */
type OrderCellAssetsData = Record<string, unknown>;

export const OrderCellAssets = ({
  data,
  isPropertySync,
  pmsSource
}: {
  data: OrderCellAssetsData;
  isPropertySync: string;
  pmsSource: string;
}) => {
  const { navigateTo } = useNavigationService();
  const [searchParams] = useSearchParams();
  const account = useSessionPmsAccount();
  const { status: integrationStatus } = useIntegrationStatus(account);
  const [showModel, setShowModel] = useState<boolean>(false);
  const [showSyncModel, setShowSyncModel] = useState<boolean>(false);
  const [showSyncInProgress, setShowSyncInProgress] = useState<boolean>(false);
  const [syncFailure, setSyncFailure] = useState<SyncFailure | null>(null);
  const [selection, setSelection] = useState<AssetSelection>({});
  dayjs.extend(localizedFormat);

  // Extract typed values from the generic data record
  const propertyId = data.propertyId as string;
  const formattedAddress = data.formattedAddress as string;
  const orderId = data.orderId as string;
  const orderStatus = data.orderStatus as string;
  const isAssetsSync = data.isAssetsSync as boolean | null;
  const property = data.property as OrderPropertyResponse | undefined;
  const services = data.services as OrderService[] | null | undefined;
  // DV-1867: the order carries its own photosetUUID (order-service now populates it). Route to that
  // specific photoset so an older order opens its own delivery — not the property's latest photoset.
  // Falls back to the latest when absent (unlinked orders), preserving prior behaviour.
  const photosetUUID = data.photosetUUID as string | undefined;
  // DV-3366: the order's photoset as order-service resolves it (its own link,
  // then a set created for it, then the set holding most of its photos). The
  // download targets this one (DV-2763).
  const resolvedPhotosetUUID = data.resolvedPhotosetUUID as
    | string
    | null
    | undefined;

  // DV-3455: the orders list's preset view rides along so the workspace's
  // Back returns to it rather than to All Orders.
  // DV-3521: the same rule as the row's Quick Actions entry (orderRowAssetsNav).
  const openAssets = (extraParams: Record<string, string> = {}) => {
    const nav = orderRowAssetsNav(
      { propertyId, orderId, photosetUUID, formattedAddress, property },
      searchParams.get('view'),
      extraParams
    );
    navigateTo(nav.path, nav.query, nav.state);
  };
  const viewAssetsAction = () => openAssets({ openAssets: 'true' });
  const navigateToAssets = () => openAssets();

  const handleModalClose = () => setShowModel(false);

  const handleSyncModalClose = () => {
    setShowSyncInProgress(false);
    setSelection({});
    setShowSyncModel(false);
  };

  const downloadAllAssets = () => setShowModel(true);

  const syncAllAssets = () => {
    if (property?.hasPhotoset && property?.propId) {
      setSelection({ propertyUUID: property.propId });
    } else {
      setSelection({ orderUUID: orderId });
    }
    // Only open the options modal here. The in-progress spinner is flipped on
    // when the sync request actually starts (inside SyncAssets), not on click —
    // otherwise the cell shows "syncing…" while the user is still choosing
    // options or before they've confirmed.
    setShowSyncModel(true);
  };

  const checkSyncConditions = () => {
    return (
      !showSyncModel &&
      !showSyncInProgress &&
      isPropertySync === 'Sync' &&
      integrationStatus.kind !== 'disabled'
    );
  };

  // DV-714: the unit-photos endpoint now supports non-Single-Family AppFolio
  // properties, so the gate on `pmsSource === 'appfolio'` + propertyType is
  // gone. Sync renders for every order with an assets surface.
  const syncAssetsSection = () => {
    // DV-3519: no sync control and no synced/failed indication for a role
    // without Sync.
    if (!canSync(getScopeRole())) return null;
    if (isAssetsSync === null) {
      return (
        <>
          {checkSyncConditions() && (
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              aria-label="Sync assets now"
              onClick={() => syncAllAssets()}
            >
              <RefreshCw className="h-[18px] w-[18px] text-muted-foreground" />
            </Button>
          )}
          {showSyncInProgress && (
            <IconComponent src={progressSpinner} alt="Syncing assets" />
          )}
        </>
      );
    } else if (isAssetsSync === true && isPropertySync === 'Sync') {
      return (
        <IconComponent src={assetsSyncSuccess} alt="syncing successfull" />
      );
    } else if (isPropertySync === 'Sync') {
      return (
        <IconComponent
          src={assetsSyncFailed}
          alt="syncing failed"
          width={24}
          height={24}
        />
      );
    }
  };

  const isDelivered = isDeliveredOrderStatus(orderStatus);

  // DV-2290: a Listing Media Asset Transfer order has no assets of its own to
  // manage, so its delivered Assets cell reads "Not Applicable" rather than
  // offering a "Manage Assets" link. The asset-transfer service slug is the
  // signal — this treatment is specific to that one service.
  const isAssetTransfer =
    services?.some(
      (service) => service.shoppingCartSlug === ASSET_TRANSFER_SLUG
    ) ?? false;

  if (isDelivered && isAssetTransfer) {
    return <Typography variant="small-medium">Not Applicable</Typography>;
  }

  if (isDelivered) {
    return (
      <>
        <section>
          <button
            type="button"
            className="text-sm font-medium text-info underline border-none bg-transparent cursor-pointer p-0"
            onClick={navigateToAssets}
          >
            Manage Assets
          </button>
          <div className="mt-1.5 flex items-center gap-2">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    aria-label="Download Assets"
                    onClick={() => downloadAllAssets()}
                  >
                    <Download className="h-[18px] w-[18px] text-muted-foreground" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="top">Download Assets</TooltipContent>
              </Tooltip>
            </TooltipProvider>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    aria-label="View Assets"
                    onClick={() => viewAssetsAction()}
                  >
                    <Eye className="h-[18px] w-[18px] text-muted-foreground" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="top">View Assets</TooltipContent>
              </Tooltip>
            </TooltipProvider>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="flex h-8 w-8 items-center justify-center">
                    {syncAssetsSection()}
                  </div>
                </TooltipTrigger>
                <TooltipContent side="top">Asset Syncing</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        </section>
        {showModel && (
          <Modal
            open={showModel}
            onOpenChange={(open) => !open && handleModalClose()}
            title="Download Assets"
            size="xl"
          >
            {/* KNOWN GAP (DV-2820), narrowed by DV-2763: when there is no
                photoset answer (no resolved set, a set on another property
                (DV-3428), or a set with no photos; see orderPhotosetDownload)
                this falls back to the order-tagged download with no hidden
                pool, so the "Include hidden photos?" opt-in does not render
                and a customer's hidden photos are silently left out of the
                ZIP. order-service's resolution is best-effort, so expect
                this on some rows. The fix is the backend photosetUUID
                download scope banked in engineering-backlog.md. */}
            <OrderDownloadAsset
              orderUUID={orderId}
              propertyUUID={propertyId}
              photosetUUID={resolvedPhotosetUUID}
              setShowModel={setShowModel}
              zipNameBase={buildZipNameBase(
                undefined,
                undefined,
                formattedAddress
              )}
            />
          </Modal>
        )}
        {showSyncModel && (
          <Modal
            open={showSyncModel}
            onOpenChange={(open) => !open && handleSyncModalClose()}
            title="Sync Assets"
            size="lg"
          >
            <SessionSyncAssets
              setShowSyncModel={setShowSyncModel}
              setSyncFailure={setSyncFailure}
              selection={selection}
              setShowSyncInProgress={setShowSyncInProgress}
              isSyncingFromAssets={false}
              pmsSource={pmsSource}
            />
          </Modal>
        )}
        {syncFailure && (
          <FailedAssetSync
            failure={syncFailure}
            onClose={() => setSyncFailure(null)}
            propertyAddress={formattedAddress}
            pmsSource={pmsSource}
            actions={ACCOUNT_FAILED_SYNC_ACTIONS}
          />
        )}
      </>
    );
  }

  return <Typography variant="small-medium">-</Typography>;
};
